import { create } from 'zustand';

// Theme store (PRD §9.4 + docs/frontend_architecture.md §4 + §9).
//
// Three modes: 'system' (follow OS), 'light', 'dark'. Persisted to
// localStorage so reloads honor the user's choice. The first-paint script
// in index.html reads the same key *before* React mounts so the page
// doesn't flash; this store re-reads it on init and keeps DOM in sync
// with subsequent setMode calls.
//
// The kit ships dark-only tokens today (`docs/frontend_architecture.md`
// §9 "Caveats"). Light selections will only diverge visually once the
// kit publishes a `[data-theme="light"]` block.

const STORAGE_KEY = 'theme';

export type ThemeMode = 'system' | 'light' | 'dark';
export type ResolvedTheme = 'light' | 'dark';

export const THEME_MODES: readonly ThemeMode[] = ['system', 'light', 'dark'];

function isThemeMode(value: unknown): value is ThemeMode {
  return value === 'system' || value === 'light' || value === 'dark';
}

export function readStoredMode(): ThemeMode {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (isThemeMode(stored)) return stored;
  } catch {
    // localStorage unavailable (private mode, sandboxed iframe, etc.).
  }
  return 'system';
}

export function resolveMode(mode: ThemeMode): ResolvedTheme {
  if (mode === 'system') {
    return window.matchMedia('(prefers-color-scheme: dark)').matches
      ? 'dark'
      : 'light';
  }
  return mode;
}

function applyTheme(mode: ThemeMode): void {
  if (typeof document === 'undefined') return;
  document.documentElement.dataset.theme = resolveMode(mode);
}

interface ThemeStore {
  mode: ThemeMode;
  setMode: (mode: ThemeMode) => void;
}

export const useThemeStore = create<ThemeStore>((set) => {
  const initial = readStoredMode();
  // Re-apply on store creation so React-mounted contexts (Vitest, SSR
  // hydration) end up consistent. The first-paint script does the same
  // job earlier in production; the writes are idempotent.
  applyTheme(initial);
  return {
    mode: initial,
    setMode: (mode) => {
      try {
        window.localStorage.setItem(STORAGE_KEY, mode);
      } catch {
        // localStorage unavailable.
      }
      applyTheme(mode);
      set({ mode });
    },
  };
});

// React to OS preference flips when in 'system' mode. Registered once at
// module load — there's only one document per page, and resetting modules
// (Vitest) yields a fresh listener against a fresh matchMedia mock.
if (typeof window !== 'undefined' && typeof window.matchMedia === 'function') {
  window
    .matchMedia('(prefers-color-scheme: dark)')
    .addEventListener('change', () => {
      const { mode } = useThemeStore.getState();
      if (mode === 'system') applyTheme('system');
    });
}
